/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : CustomDiv.jsx
 * description   : {{insert here}}
 * creation date : 18/01/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    Fragment,
} from 'react';

import {
    Flex,
    View,
    Button,
    MenuItem,
    IconsProvider,
    Message,
} from '@aws-amplify/ui-react';

import {
    useLockBodyScroll,
} from '@uidotdev/usehooks';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    auth_routes,
} from '@/utils/router/core';

import {
    FcMediumPriority,
    FcHighPriority,
    FcInfo,
    FcOk,
    FcMinus,
} from 'react-icons/fc';

import {
    LinkButton,
} from '@/components/snippets/RouterLink';

import fuses from '@/styles/app/fuses.scss';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function ViewPortContainer({ viewPort = toolKit.enums.window.viewPortTypes.LARGE, children, ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    return (
        <Fragment>
            <Flex
                width={ctx.theme.tokens.space.relative.full}
                justifyContent='center'
                {...rest}
            >
                <View
                    width={ctx.theme.tokens.space.relative.full}
                    maxWidth={fuses[viewPort]}
                >
                    {children}
                </View>
            </Flex>
        </Fragment>
    );
}

function ButtonNavigateToSignIn({ ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    return (
        <Fragment>
            <LinkButton
                to={
                    ctx.api.utils.buildUrlAuth(
                        auth_routes.SIGN_IN,
                        true
                    )
                }
                {...rest}
            >
                {/* TODO: Translate message */}
                Sign In
            </LinkButton>
        </Fragment>
    );
}

function ButtonSignOut({ ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    function handleOnClick_signOut(event) {
        event.preventDefault();

        // Call the asynchronous process
        var proc_call = ctx.api.auth.signOut();

        // Wait for the result, then process it
        proc_call.then(proc_status => {
            // Unsuccessful call
            if (proc_status !== toolKit.enums.auth.procStatusTypes.SUCCESS) {
                console.error(proc_status);
            }
        });
    }

    return (
        <Fragment>
            <Button
                width='fit-content'
                onClick={handleOnClick_signOut}
                {...rest}
            >
                {/* TODO: Translate message */}
                Sign Out
            </Button>
        </Fragment>
    );
}

function MenuItemNavigate({ to, children, ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    return (
        <Fragment>
            <MenuItem
                onClick={() =>
                    ctx.api.location.navigate(to)
                }
                {...rest}
            >
                {children}
            </MenuItem>
        </Fragment>
    );
}

function MenuItemNavigateToSignIn({ ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    return (
        <Fragment>
            <MenuItem
                onClick={() =>
                    ctx.api.location.navigate(
                        ctx.api.utils.buildUrlAuth(
                            auth_routes.SIGN_IN,
                            true
                        )
                    )
                }
                {...rest}
            >
                {/* TODO: Translate message */}
                Sign In
            </MenuItem>
        </Fragment>
    );
}

function MenuItemSignOut({ ...rest }) {
    // Component context
    const { ctx } = useComponentContext();

    function handleOnClick_signOut(event) {
        event.preventDefault();

        // Call the asynchronous process
        var proc_call = ctx.api.auth.signOut();

        // Wait for the result, then process it
        proc_call.then(proc_status => {
            // Unsuccessful call
            if (proc_status !== toolKit.enums.auth.procStatusTypes.SUCCESS) {
                console.error(proc_status);
            }
        });
    }

    return (
        <Fragment>
            <MenuItem
                onClick={handleOnClick_signOut}
                {...rest}
            >
                {/* TODO: Translate message */}
                Sign Out
            </MenuItem>
        </Fragment>
    );
}

function ModalLockBodyScroll({ children }) {
    useLockBodyScroll();

    return (
        <Fragment>
            {children}
        </Fragment>
    );
}

function MessageCustomIcon({ children, ...rest }) {
    return (
        <Fragment>
            <IconsProvider
                icons={{
                    message: {
                        info: <FcInfo />,
                        success: <FcOk />,
                        error: <FcHighPriority />,
                        warning: <FcMediumPriority />,
                        close: <FcMinus />,
                    },
                }}
            >
                <Message
                    {...rest}
                >
                    {children}
                </Message>
            </IconsProvider>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export {
    ViewPortContainer,
    ButtonNavigateToSignIn,
    ButtonSignOut,
    MenuItemNavigate,
    MenuItemNavigateToSignIn,
    MenuItemSignOut,
    ModalLockBodyScroll,
    MessageCustomIcon,
};



// End of file
